import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useDomHandlerContext,
  SCOPE_ID,
} from './../components/handlers/DomHandler'; // adjust import

// --- Utils: parse "@container (max-width: 300px)" into a simple condition ---
const parseContainerQuery = (query) => {
  if (!query) return null;
  const match = query.match(/(max|min)-width:\s*(\d+)/i);
  if (!match) return null;
  const type = match[1].toLowerCase(); // 'max' or 'min'
  const size = parseInt(match[2], 10);
  const operator = type === 'max' ? '<=' : '>';
  return { operator, size };
};

// --- Main hook ---
export const useContainerQuery = (query) => {
  const [matches, setMatches] = useState(false);
  const parsedQuery = useMemo(() => parseContainerQuery(query), [query]);

  // Access the ShadowRoot and Document via UiScopeContext
  const {
    shadowRoot = null,
    doc = typeof document !== 'undefined' ? document : null,
  } = useDomHandlerContext();

  // Refs to manage observers and the current container element
  const containerRef = useRef(null);
  const resizeObsRef = useRef(null);
  const mutationObsRef = useRef(null);

  useEffect(() => {
    if (!parsedQuery) {
      console.error('Invalid container query format provided.');
      return;
    }
    const root =
      shadowRoot || doc || (typeof document !== 'undefined' ? document : null);
    if (!root) return;

    const safeEscape = (id) =>
      window.CSS && CSS.escape
        ? CSS.escape(id)
        : id.replace(/([^\w-])/g, '\\$1');

    // Find the container inside the ShadowRoot first; fall back to light DOM
    const findContainerEl = () => {
      if (shadowRoot && typeof shadowRoot.querySelector === 'function') {
        const el = shadowRoot.querySelector(`#${safeEscape(SCOPE_ID)}`);
        if (el) return el;
      }
      if (doc && typeof doc.getElementById === 'function') {
        return doc.getElementById(SCOPE_ID);
      }
      return null;
    };

    // Evaluate condition
    const checkMatch = (width) => {
      const { operator, size } = parsedQuery;
      setMatches(operator === '<=' ? width <= size : width > size);
    };

    // Attach ResizeObserver to the element
    const setupResizeObserver = (target) => {
      if (!target) return;
      containerRef.current = target;

      // Initial measure
      const initialWidth =
        (target.getBoundingClientRect &&
          target.getBoundingClientRect().width) ||
        target.clientWidth ||
        0;
      checkMatch(initialWidth);

      // Observe future changes
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width =
            // Prefer borderBoxSize inlineSize if available
            (entry.borderBoxSize &&
              entry.borderBoxSize[0] &&
              entry.borderBoxSize[0].inlineSize) ||
            (entry.contentRect && entry.contentRect.width) ||
            entry.target.clientWidth;
          checkMatch(width);
        }
      });
      resizeObsRef.current = ro;
      ro.observe(target);
    };

    // Try to find the element now
    let el = findContainerEl();

    if (el) {
      setupResizeObserver(el);
    } else {
      // If not found, watch for it to appear
      const moTarget =
        (shadowRoot && shadowRoot) ||
        (doc && (doc.body || doc.documentElement)) ||
        null;

      if (moTarget && typeof MutationObserver !== 'undefined') {
        const mo = new MutationObserver(() => {
          el = findContainerEl();
          if (el) {
            setupResizeObserver(el);
            mo.disconnect();
            mutationObsRef.current = null;
          }
        });
        mutationObsRef.current = mo;
        mo.observe(moTarget, { childList: true, subtree: true });
        // Optional: log once to aid debugging
        // console.warn(`Container element with ID "${SCOPE_ID}" not found yet. Waiting...`);
      } else {
        // As a last resort, do a one-off check on next tick
        const rAF =
          typeof requestAnimationFrame !== 'undefined'
            ? requestAnimationFrame
            : (fn) => setTimeout(fn, 16);
        rAF(() => {
          el = findContainerEl();
          if (el) setupResizeObserver(el);
        });
      }
    }

    // Cleanup observers
    return () => {
      if (resizeObsRef.current && containerRef.current) {
        try {
          resizeObsRef.current.unobserve(containerRef.current);
        } catch {
          /* empty */
        }
        try {
          resizeObsRef.current.disconnect();
        } catch {
          /* empty */
        }
      }
      if (mutationObsRef.current) {
        try {
          mutationObsRef.current.disconnect();
        } catch {
          /* empty */
        }
      }
      resizeObsRef.current = null;
      mutationObsRef.current = null;
      containerRef.current = null;
    };
  }, [parsedQuery, shadowRoot, doc]);

  return matches;
};
